import React, { useState, useEffect, useRef, useCallback } from "react";
import { ChatCircleDots, X, PaperPlaneRight, ArrowsClockwise } from "@phosphor-icons/react";

const SESSION_KEY = "pitchiq_chat_session";
const HISTORY_KEY = "pitchiq_chat_history";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const loadHistory = () => {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
};

const saveHistory = (msgs) => {
  try { localStorage.setItem(HISTORY_KEY, JSON.stringify(msgs.slice(-60))); } catch { /* noop */ }
};

const loadSessionId = () => {
  try { return localStorage.getItem(SESSION_KEY) || null; } catch { return null; }
};

const saveSessionId = (sid) => {
  try { localStorage.setItem(SESSION_KEY, sid); } catch { /* noop */ }
};

export const ChatBubble = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(loadHistory);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(loadSessionId);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { saveHistory(messages); }, [messages]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, open]);
  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  const ensureSession = useCallback(async () => {
    if (sessionId) return sessionId;
    const res = await fetch(`${BACKEND_URL}/api/chat/start`, { method: "POST" });
    const data = await res.json();
    saveSessionId(data.session_id);
    setSessionId(data.session_id);
    return data.session_id;
  }, [sessionId]);

  const reset = async () => {
    if (sessionId) {
      try { await fetch(`${BACKEND_URL}/api/chat/session/${sessionId}`, { method: "DELETE" }); } catch { /* noop */ }
    }
    try { localStorage.removeItem(SESSION_KEY); localStorage.removeItem(HISTORY_KEY); } catch { /* noop */ }
    setSessionId(null);
    setMessages([]);
  };

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;

    const userMsg = { role: "user", content: text, at: Date.now() };
    setMessages((m) => [...m, userMsg]);
    setInput("");
    setSending(true);

    // Insert empty assistant placeholder that we'll fill token-by-token
    setMessages((m) => [...m, { role: "assistant", content: "", at: Date.now(), streaming: true }]);

    try {
      const sid = await ensureSession();
      const res = await fetch(`${BACKEND_URL}/api/chat/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: sid }),
      });
      if (!res.ok || !res.body) throw new Error("Network error");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const evt = JSON.parse(line.slice(6));
            if (evt.type === "delta") {
              setMessages((m) => {
                const copy = m.slice();
                const last = copy[copy.length - 1];
                if (last && last.role === "assistant") last.content += evt.content;
                return copy;
              });
            } else if (evt.type === "error") {
              setMessages((m) => {
                const copy = m.slice();
                const last = copy[copy.length - 1];
                if (last && last.role === "assistant") {
                  last.content = "⚠ " + evt.message;
                  last.streaming = false;
                }
                return copy;
              });
            } else if (evt.type === "done") {
              setMessages((m) => {
                const copy = m.slice();
                const last = copy[copy.length - 1];
                if (last) last.streaming = false;
                return copy;
              });
            }
          } catch { /* malformed line — skip */ }
        }
      }
    } catch (err) {
      setMessages((m) => {
        const copy = m.slice();
        const last = copy[copy.length - 1];
        if (last && last.role === "assistant") {
          last.content = "⚠ Couldn't reach the chat right now. Try again in a moment.";
          last.streaming = false;
        }
        return copy;
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating launcher button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chat-launcher"
          aria-label="Open chat"
          className="fixed bottom-24 md:bottom-5 right-5 z-[70] bg-primary-yellow text-black rounded-full p-4 shadow-[0_8px_24px_rgba(227,255,0,0.35)] hover:scale-110 active:scale-95 transition-transform"
        >
          <ChatCircleDots size={26} weight="fill" />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed z-[80] bottom-0 right-0 sm:bottom-5 sm:right-5 w-full sm:w-[380px] h-[80vh] sm:h-[560px] surface border border-white/15 sm:border-primary-yellow/30 bg-[#0F1116] flex flex-col shadow-[0_-8px_24px_rgba(0,0,0,0.5)] sm:shadow-[0_8px_24px_rgba(0,0,0,0.5)]"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-[#0B0D11]">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 grid place-items-center bg-primary-yellow text-black rounded-full">
                <ChatCircleDots size={18} weight="fill" />
              </div>
              <div>
                <div className="font-heading text-sm uppercase font-black leading-tight">PitchIQ Concierge</div>
                <div className="text-[9px] uppercase tracking-[0.2em] text-white/40">Powered by AI · 24/7</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {messages.length > 0 && (
                <button
                  onClick={reset}
                  data-testid="chat-reset"
                  aria-label="Reset chat"
                  title="Reset conversation"
                  className="text-white/40 hover:text-white p-1.5"
                >
                  <ArrowsClockwise size={16} />
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                data-testid="chat-close"
                aria-label="Close chat"
                className="text-white/40 hover:text-white p-1.5"
              >
                <X size={18} weight="bold" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
            {messages.length === 0 && (
              <div className="text-center py-8">
                <ChatCircleDots size={32} weight="duotone" className="text-primary-yellow/60 mx-auto" />
                <div className="font-heading text-xl uppercase font-black mt-4">How can I help?</div>
                <p className="text-xs text-white/50 mt-2 px-4">
                  Ask about scoring, Premier perks, refunds, anything PitchIQ.
                </p>
                <div className="mt-5 flex flex-col gap-2 px-2">
                  {[
                    "How does scoring work?",
                    "What do I get with Premier?",
                    "How do private leagues work?",
                  ].map((q) => (
                    <button
                      key={q}
                      onClick={() => { setInput(q); setTimeout(() => send(), 50); }}
                      className="text-xs text-left bg-white/5 hover:bg-primary-yellow/10 hover:text-primary-yellow border border-white/10 hover:border-primary-yellow/40 px-3 py-2 transition-colors"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                data-testid={`chat-msg-${m.role}`}
              >
                <div
                  className={`max-w-[85%] px-3 py-2 text-sm whitespace-pre-wrap leading-snug ${
                    m.role === "user"
                      ? "bg-primary-yellow text-black"
                      : "bg-white/[0.04] border border-white/10 text-white/90"
                  }`}
                >
                  {m.content || (m.streaming && <span className="opacity-40">…</span>)}
                  {m.role === "assistant" && m.streaming && m.content && (
                    <span className="inline-block w-1.5 h-3 bg-primary-yellow/70 ml-0.5 align-middle animate-pulse" />
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Input */}
          <form onSubmit={send} className="border-t border-white/10 p-3 bg-[#0B0D11] flex gap-2">
            <input
              ref={inputRef}
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask anything…"
              maxLength={2000}
              disabled={sending}
              className="flex-1 bg-[#0F1116] border border-white/10 px-3 py-2 text-sm focus:border-primary-yellow focus:outline-none disabled:opacity-50"
            />
            <button
              type="submit"
              data-testid="chat-send"
              disabled={sending || !input.trim()}
              className="bg-primary-yellow text-black px-4 py-2 hover:-translate-y-0.5 transition-transform disabled:opacity-40 disabled:hover:translate-y-0"
              aria-label="Send"
            >
              <PaperPlaneRight size={18} weight="bold" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatBubble;
